import React, { useContext } from 'react';
import uuid from 'uuid-random';
import {
  StyledSectionLabel,
  StyledSection,
  StyledText,
  LineBreak,
  StyledHighlightedWord,
  SpacedSpan,
  StyledTitle,
  StyledWordsLine,
  StyledSpace,
} from '../../Styled';
import {
  Section,
  ContentLine,
  Image,
  Highlighted,
  ExemplarWord,
  ProcedureContentLine,
  TextTypes,
  WordWithLanguageFeatures,
  SimpleLanguageFeatureWord,
} from '../../../types';
import { getColorByPartOfSpeech, getStyles, isWordUnderlined } from '../../../utils';
import { COLORS, EXEMPLAR_MODES, EXTENDING, PHRASES, PREPOSITIONAL_PHRASES } from '../../../constants';
import { StyledWordContext } from '../../../contexts/styledWordContext';
import { CLICKABLE_WORD, ClickableWord } from '../../ClickableWord';
import { ProcedureItemList } from '../../List/ProcedureItemList';
import {
  getLineWithLanguageFeatures,
  getLineWithTextPresentation,
  getSectionColor,
  getTextAlignForSection,
} from '../../../utils/exemplar';
import { ExemplarContext } from '../../../contexts/laterContext';

interface ILaterSection {
  section: Section;
  sectionIndex?: number;
  hidden?: boolean;
  highlighted?: Highlighted;
  mode: string;
  isPreviewModal?: boolean;
  colors?: {
    title: string;
    text: string;
  };
  side?: string;
  editMode: boolean;
  textType?: string;
  isAccessible?: boolean;
  stage: string;
  handleFontStyle?: (word: ExemplarWord) => void;
}

// eslint-disable-next-line complexity
export const LaterSection: React.FC<ILaterSection> = (props): JSX.Element => {
  const {
    section,
    sectionIndex,
    hidden,
    highlighted,
    colors,
    mode,
    isPreviewModal,
    side,
    editMode,
    textType,
    isAccessible,
    stage,
    handleFontStyle,
  } = props;
  const { label, type, lines, style } = section;

  const isProcedure = textType === TextTypes.procedure;

  const { handleTool, styledWordState } = useContext(StyledWordContext);
  const { handleWordHighlight, saveHighlightsUpdates } = useContext(ExemplarContext);

  const textColor = mode === EXEMPLAR_MODES.textPresentation ? getSectionColor(section) : undefined;
  const textAlign = getTextAlignForSection(style);
  const isSmall = sectionIndex === 0 && !label && textAlign === 'right';
  const isHighlighted = label && highlighted ? highlighted[label] : false;
  const isHidden = label === 'Title' && side === undefined ? true : styledWordState.isHiddenTextMode;
  const isLanguageFeaturesPage = mode === EXEMPLAR_MODES.languageFeatures;
  const isHighlightingMode = styledWordState.isHighlightingMode;
  const isWordClickable = editMode && (isLanguageFeaturesPage || isHighlightingMode);

  const getPadding = (word: ExemplarWord): string => {
    const isWordPhraseOrLanguage = word.phrase || word.prepositional_phrase || word.language;

    return (isLanguageFeaturesPage || isPreviewModal) && isWordPhraseOrLanguage && !word.style?.includes('nospacing')
      ? '0 2px 0 2px'
      : '0';
  };

  const getStyledWord = (word: ExemplarWord) => {
    const wordStyles = getStyles(word?.style);
    const { language } = word;
    const languageHighlighted =
      isPreviewModal || (!!highlighted && highlighted[language as string] && isLanguageFeaturesPage);

    const padding = getPadding(word);

    const spacesSpan = (
      <SpacedSpan
        data-testid="spaced-span"
        className={CLICKABLE_WORD}
        key={word.id}
        wordStyles={wordStyles}
        isHidden={isHidden}
        padding={padding}
      >
        {word.text === '\n\n' ? <br /> : word.text}
      </SpacedSpan>
    );

    return isHidden ? (
      spacesSpan
    ) : (
      <ClickableWord
        page={mode}
        languageHighlighted={languageHighlighted}
        handleWordHighlight={handleWordHighlight}
        saveHighlightsUpdates={saveHighlightsUpdates}
        handleTool={handleTool}
        handleFontStyle={handleFontStyle}
        editMode={editMode}
        isClickable={isWordClickable}
        word={word}
        key={word.id}
      >
        {spacesSpan}
      </ClickableWord>
    );
  };

  // eslint-disable-next-line complexity
  const getWordWithLangFeatures = (wordElement: JSX.Element, word: WordWithLanguageFeatures) => {
    const { language, type, isPhrase, isPrepositionalPhrase } = word;
    if (!highlighted && !isPreviewModal) return;

    const isHighlighted = isPreviewModal || (!isHidden && !!highlighted && highlighted[language as string]);
    const isUnderlined = !isHidden && isPhrase && !!highlighted && highlighted[PHRASES];
    const isDottedUnderlined =
      !isHidden && isPrepositionalPhrase && !!highlighted && highlighted[PREPOSITIONAL_PHRASES];

    return (
      <StyledHighlightedWord
        key={uuid()}
        data-testid="lf-highlighted-word"
        isLangFeatures
        isSpace={type === 'space'}
        isClickable={isWordClickable}
        color={getColorByPartOfSpeech(language as string)}
        highlighted={isHighlighted}
        isUnderlined={isUnderlined}
        isDottedUnderlined={isDottedUnderlined}
      >
        {wordElement}
      </StyledHighlightedWord>
    );
  };

  const getSpaceBetweenWords = (isUnderlined: boolean, width?: number) => (
    <StyledSpace isLanguageFeaturesPage={isLanguageFeaturesPage} isUnderlined={isUnderlined} width={width} key={uuid()}>
      <span> </span>
    </StyledSpace>
  );

  const renderWordsWithLanguageFeatures = (words: ExemplarWord[]) => {
    const line = getLineWithLanguageFeatures(words);

    // eslint-disable-next-line complexity
    return line.map((word, index) => {
      let wordElement;

      if (word.type === 'word' && (highlighted || isPreviewModal)) {
        wordElement = getStyledWord(word.word);
      } else {
        const defaultSpaceWidth = 7.3;
        const languageWordSidePadding = 2;

        const isLastWordInLine = index === line.length - 1;
        const isFirstWordInLine = index === 0;

        const previousWord = isFirstWordInLine ? undefined : line[index - 1];
        const nextWord = isLastWordInLine ? undefined : line[index + 1];

        const isPreviousBreakLine = previousWord && (previousWord as SimpleLanguageFeatureWord).word.text === '\n\n';
        const isNextBreakLine = nextWord && (nextWord as SimpleLanguageFeatureWord).word.text === '\n\n';

        const isPreviousWordAnyLanguageOrPhrase =
          previousWord?.isPhrase || previousWord?.isPrepositionalPhrase || previousWord?.language;
        const isNextWordAnyLanguageOrPhrase = isLastWordInLine
          ? false
          : nextWord?.isPhrase || nextWord?.isPrepositionalPhrase || nextWord?.language;

        if (isPreviousBreakLine || isNextBreakLine) {
          wordElement = <></>;
        } else if (!(isPreviousWordAnyLanguageOrPhrase || isNextWordAnyLanguageOrPhrase) || stage !== EXTENDING) {
          wordElement = getSpaceBetweenWords(false);
        } else if (isPreviousWordAnyLanguageOrPhrase && isNextWordAnyLanguageOrPhrase) {
          wordElement = getSpaceBetweenWords(false, defaultSpaceWidth - languageWordSidePadding * 2);
        } else {
          wordElement = getSpaceBetweenWords(false, defaultSpaceWidth - languageWordSidePadding);
        }
      }

      if (word.language || word.isPrepositionalPhrase || word.isPhrase) {
        return getWordWithLangFeatures(wordElement, word);
      }

      return wordElement;
    });
  };

  const renderWordsWithTextPresentation = (words: ExemplarWord[]) => {
    const line = getLineWithTextPresentation(words);

    return line.map(word => {
      return (
        <span className={CLICKABLE_WORD} key={uuid()}>
          {word.type === 'space'
            ? getSpaceBetweenWords(isWordUnderlined(word.text_type, mode, styledWordState.isUnderlineHiddenMode))
            : ''}
          {word.type === 'word' && getStyledWord(word.word)}
        </span>
      );
    });
  };

  const renderWords = (words: ExemplarWord[]) => {
    if (isLanguageFeaturesPage || isPreviewModal) {
      return renderWordsWithLanguageFeatures(words);
    }

    return renderWordsWithTextPresentation(words);
  };

  const renderTextLines = (lines: Array<ContentLine | ProcedureContentLine>) => {
    // eslint-disable-next-line complexity
    return lines.map((line: ContentLine | ProcedureContentLine, index) => {
      const isHeading = Array.isArray(style) && style.includes('heading');

      if (!line.words.length) {
        return <LineBreak key={uuid()} data-testid="line-break" />;
      }

      const getWordsLineMargin = (isProcedure: boolean, isFirstLineInSection: boolean): string => {
        if (!isProcedure) return '0 0 2px 0';

        if (isFirstLineInSection) return '7px 0 0 0';

        return '0';
      };

      const styles = 'style' in line ? (line.style as Array<string>) : [''];
      const sequenceNumber = 'sequence_number' in line ? `${line.sequence_number}` : '';
      const color = mode === EXEMPLAR_MODES.textPresentation && isProcedure ? COLORS.venetianRed : COLORS.black;

      return (
        <ProcedureItemList styles={styles} sequenceNumber={sequenceNumber} color={color} stage={stage} key={uuid()}>
          {isHeading ? (
            <StyledTitle data-testid="later-title" stage={stage} color={textColor} key={uuid()}>
              {renderWords(line.words)}
            </StyledTitle>
          ) : (
            <StyledWordsLine key={uuid()} margin={getWordsLineMargin(isProcedure, index === 1)}>
              {renderWords(line.words)}
            </StyledWordsLine>
          )}
        </ProcedureItemList>
      );
    });
  };

  const renderSectionContent = (lines: ContentLine[] | Image) => {
    if (Array.isArray(lines)) {
      return renderTextLines(lines);
    }
  };

  return (
    <>
      {type === 'section' && (
        <StyledSection
          data-testid="section"
          textAlign={textAlign}
          bgColor={colors?.text}
          hidden={!!hidden}
          isSmall={isSmall}
          highlight={isHighlighted}
          isStructureMode={mode === EXEMPLAR_MODES.textStructure}
          isAccessible={isAccessible}
        >
          <StyledSectionLabel
            data-testid="section-label"
            highlight={isHighlighted}
            bgColor={colors?.title}
            hidden={!!hidden}
            wrapLabel={lines.length <= 4 && !!label?.includes(' ')}
            isStructureMode={mode === EXEMPLAR_MODES.textStructure}
          >
            <div>{label}</div>
          </StyledSectionLabel>
          {Array.isArray(lines) && (
            <StyledText data-testid="section-text" textColor={textColor} isHidden={isHidden}>
              {renderSectionContent(lines)}
            </StyledText>
          )}
        </StyledSection>
      )}
    </>
  );
};
